import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import {
  SearchSuccessResponse,
  SearchListing,
  validateSearchResponse,
} from '../schemas/search.schema';
import { TagSearchResponseSchema } from '../schemas/api/search';
import { fetchSearchList, fetchOtherListings, pickDataForListing } from '../data/api/search';
import { LS_Get, LS_Set } from '../utils/local-storage';
import { trackError, ErrorSeverity } from '../utils/error-tracking';
import { filterKey, filtersFromQuery, hasActiveFilters } from '../utils/search-filter-state';
import { appendPageLoadAt, appendShuffleSeed } from '../utils/seeded-shuffle';

export interface SearchFilters {
  q?: string;
  categoryId?: number | number[];
  categoryGroupId?: number;
  excludeCategoryId?: number | number[];
  excludeCategoryGroupId?: number | number[];
  minPrice?: number;
  maxPrice?: number;
  itemCondition?: number[] | null;
  userId?: number;
  dataSourceId?: number;
  shippable?: boolean;
  sold?: boolean;
  status?: number;
  tags?: string[];
  /** false: no per-seller step-down (a Shop tag page, utils/seller-diversity.ts). */
  sellerCap?: boolean;
  page?: number;
  limit?: number;
  featured?: boolean;
  /** Deals Only / Quality Only (utils/deal-quality-filter.ts). */
  deals?: boolean;
  quality?: boolean;
  /** Listing attribute filters (utils/search-filter-state.ts); each matches any of its values. */
  gender?: string[];
  ageGroup?: string[];
  size?: string[];
  color?: string[];
  brand?: string[];
  material?: string[];
  /** Tag and type filters (utils/tag-facet-param.ts). */
  tf?: string;
  isCategoryGroup?: boolean;
  /** Use the home-listings endpoint (excludes Pets, Miscellaneous, Jobs). */
  isHomePage?: boolean;
  /** Shopper-picked order (utils/listing-sort.ts). Absent means Best match. */
  sortBy?: string;
  // Legacy compatibility
  min?: number;
  max?: number;
  category?: string;
  latitude?: string;
  longitude?: string;
}

// Alias for backward compatibility
export type SearchParams = SearchFilters;

export interface RecentCategory {
  id: number;
  name: string;
  slug?: string;
  count?: number;
}

// Use Zod-validated type for search results
export type SearchResult = SearchSuccessResponse;

export interface SearchContextType {
  // State
  results: SearchListing[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  filters: SearchFilters;

  // Search state (from ListingProvider)
  searchParams: SearchFilters;
  searchHistory: SearchFilters[];
  searchEmpty: boolean;
  currentCategoryGroupId: number;

  // Recent items (from ListingProvider)
  recentSearches: SearchFilters[];
  recentCategories: RecentCategory[];

  // Actions
  searchListings: (filters: SearchFilters) => Promise<SearchResult>;
  searchByTag: (tag: string, additionalFilters?: Partial<SearchFilters>) => Promise<SearchResult>;
  clearSearch: () => void;
  loadMore: () => Promise<void>;
  setFilters: (filters: SearchFilters) => void;

  // Search actions (from ListingProvider)
  getSearchList: (params: SearchFilters, replaceCurrentList?: (list: Record<string, unknown>[], params?: Record<string, unknown>) => void) => Promise<void>;
  getFreshList: (params: SearchFilters, replaceCurrentList?: (list: Record<string, unknown>[], params?: Record<string, unknown>) => void) => Promise<void>;
  getFreshTagList: (tag: string, params?: Partial<SearchFilters>, replaceCurrentList?: (list: Record<string, unknown>[], params?: Record<string, unknown>) => void) => Promise<void>;
  setSearchParams: (params: SearchFilters) => void;
  setSearchEmpty: (empty: boolean) => void;
  setCurrentCategoryGroupId: (id: number) => void;

  // Recent item actions (from ListingProvider)
  setRecentSearch: (params: SearchFilters) => void;
  setRecentCategory: (category: RecentCategory) => void;
  removeRecentSearch: (query: string) => void;
  removeRecentCategory: (id: number) => void;

  // Job and user listings
  fetchJobList: (q?: string, page?: number) => Promise<Record<string, unknown>[]>;
  getUserListings: (userId: string, productId: string, page?: number) => Promise<{ listings: Record<string, unknown>[]; user: Record<string, unknown> }>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
  initialCategoryGroupId?: number;
}

/**
 * Is this worth keeping as a recent search? It needs something to go back
 * to: a query, a category, or any filter. A filters-only search counts: the
 * shopper uses recent searches to click between filter sets.
 */
export const isValidSearch = (search: SearchFilters): boolean => {
  if (!search) return false;
  const hasQuery = Boolean(search.q && search.q.trim().length > 0);
  const hasCategory = Boolean(search.categoryGroupId && search.categoryGroupId > 0);
  return hasQuery || hasCategory || hasActiveFilters(filtersFromQuery(search as Record<string, never>));
};

/** Two searches are the same recent search when their query and every filter match. */
export const recentSearchKey = (search: SearchFilters): string =>
  JSON.stringify([(search.q ?? '').trim().toLowerCase(), filterKey(filtersFromQuery(search as Record<string, never>))]);

/**
 * Normalizes a search object, keeping data intact
 */
const normalizeSearch = (search: SearchFilters): SearchFilters => {
  const normalized = { ...search };

  // Keep itemCondition as-is (can be number, array, or null)
  // Just filter out invalid array entries
  if (Array.isArray(normalized.itemCondition)) {
    const validConditions = normalized.itemCondition.filter(c => c > 0);
    normalized.itemCondition = validConditions.length > 0 ? validConditions : null;
  }

  return normalized;
};

export const SearchProvider: React.FC<SearchProviderProps> = ({
  children,
  initialCategoryGroupId = 0,
}) => {
  // Modern search state
  const [results, setResults] = useState<SearchListing[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(32);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState<SearchFilters>({});

  // Refs for stable callback access (avoids re-creating loadMore on every state change)
  const pageRef = useRef(page);
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  const filtersRef = useRef(filters);

  // Keep refs in sync with the latest state after each render. These refs are only
  // read inside callbacks/effects (e.g. loadMore), so syncing in an effect preserves behavior.
  useEffect(() => {
    pageRef.current = page;
    hasMoreRef.current = hasMore;
    loadingRef.current = loading;
    filtersRef.current = filters;
  }, [page, hasMore, loading, filters]);

  // Legacy search state (from ListingProvider)
  const [searchParams, setSearchParamsState] = useState<SearchFilters>({
    categoryGroupId: initialCategoryGroupId,
    q: '',
    min: 0,
    max: 0,
    page: 1,
  });
  const [searchHistory, setSearchHistory] = useState<SearchFilters[]>([]);
  const [searchEmpty, setSearchEmpty] = useState(false);
  const [currentCategoryGroupId, setCurrentCategoryGroupId] = useState(initialCategoryGroupId);

  // Recent items (from ListingProvider)
  const [recentSearches, setRecentSearchesState] = useState<SearchFilters[]>([]);
  // Every entry in state carries its click count: legacy saved entries get 1
  // when they are loaded, new ones start at 1.
  const [recentCategories, setRecentCategoriesState] = useState<Array<RecentCategory & { count: number }>>([]);

  // Load saved data from localStorage after mount
  useEffect(() => {
    // Load and validate recent searches - clean up invalid entries
    const savedRecentSearches = LS_Get<SearchFilters[]>('recentSearches');
    if (savedRecentSearches && Array.isArray(savedRecentSearches)) {
      const validSearches = savedRecentSearches
        .map(normalizeSearch)
        .filter(isValidSearch)
        .slice(0, 10);

      if (validSearches.length > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- mount-only hydration from localStorage (client-only); a lazy initializer would cause SSR hydration mismatch.
        setRecentSearchesState(validSearches);
        if (validSearches.length !== savedRecentSearches.length) {
          LS_Set('recentSearches', validSearches);
        }
      } else if (savedRecentSearches.length > 0) {
        setRecentSearchesState([]);
        LS_Set('recentSearches', []);
      }
    }

    const savedRecentCategories = LS_Get<RecentCategory[]>('recentCategories');
    if (savedRecentCategories && Array.isArray(savedRecentCategories)) {
      // Entries saved before click-count scoring have no count; treat them as 1.
      // The sort is stable, so legacy entries keep their saved order.
      const scored = savedRecentCategories
        .map(c => ({ ...c, count: c.count ?? 1 }))
        .sort((a, b) => b.count - a.count);
      setRecentCategoriesState(scored);
    }

    // Load and validate search history
    const savedSearchHistory = LS_Get<SearchFilters[]>('searchHistory');
    if (savedSearchHistory && Array.isArray(savedSearchHistory)) {
      const validHistory = savedSearchHistory
        .map(normalizeSearch)
        .filter(isValidSearch)
        .slice(0, 20);

      if (validHistory.length > 0) {
        setSearchHistory(validHistory);
        if (validHistory.length !== savedSearchHistory.length) {
          LS_Set('searchHistory', validHistory);
        }
      } else if (savedSearchHistory.length > 0) {
        setSearchHistory([]);
        LS_Set('searchHistory', []);
      }
    }
  }, []);

  /**
   * Build query string from search filters
   */
  const buildQueryString = useCallback((searchFilters: SearchFilters): string => {
    const params = new URLSearchParams();

    // Text query
    if (searchFilters.q && searchFilters.q.trim().length > 0) {
      params.append('q', searchFilters.q.trim());
    }

    // Category filters
    if (searchFilters.categoryGroupId) {
      params.append('categoryGroupId', String(searchFilters.categoryGroupId));
    }

    if (searchFilters.categoryId) {
      if (Array.isArray(searchFilters.categoryId)) {
        params.append('categoryId', searchFilters.categoryId.join(','));
      } else {
        params.append('categoryId', String(searchFilters.categoryId));
      }
    }

    // Category exclusion filters
    if (searchFilters.excludeCategoryId) {
      if (Array.isArray(searchFilters.excludeCategoryId)) {
        params.append('excludeCategoryId', searchFilters.excludeCategoryId.join(','));
      } else {
        params.append('excludeCategoryId', String(searchFilters.excludeCategoryId));
      }
    }

    if (searchFilters.excludeCategoryGroupId) {
      if (Array.isArray(searchFilters.excludeCategoryGroupId)) {
        params.append('excludeCategoryGroupId', searchFilters.excludeCategoryGroupId.join(','));
      } else {
        params.append('excludeCategoryGroupId', String(searchFilters.excludeCategoryGroupId));
      }
    }

    // Price filters
    if (searchFilters.minPrice !== undefined && searchFilters.minPrice > 0) {
      params.append('minPrice', String(searchFilters.minPrice));
    }

    if (searchFilters.maxPrice !== undefined && searchFilters.maxPrice > 0) {
      params.append('maxPrice', String(searchFilters.maxPrice));
    }

    // Item condition filter
    if (searchFilters.itemCondition && searchFilters.itemCondition.length > 0) {
      params.append('itemCondition', searchFilters.itemCondition.join(','));
    }

    // Tags filter — emit each tag as its own `tags=` param so that tag values
    // containing commas survive intact (the server's comma-split fallback only
    // runs on string values).
    if (searchFilters.sellerCap === false) params.append('sellerCap', 'off');
    if (searchFilters.tags && searchFilters.tags.length > 0) {
      searchFilters.tags.forEach(tag => params.append('tags', tag));
    }

    if (searchFilters.tf) {
      params.append('tf', searchFilters.tf);
    }

    // Boolean filters
    if (searchFilters.shippable !== undefined) {
      params.append('shippable', searchFilters.shippable ? 'true' : 'false');
    }

    if (searchFilters.sold !== undefined) {
      params.append('sold', searchFilters.sold ? 'true' : 'false');
    }

    if (searchFilters.featured) params.append('featured', 'true');
    if (searchFilters.deals) params.append('deals', 'true');
    if (searchFilters.quality) params.append('quality', 'true');

    // ID filters
    if (searchFilters.userId) {
      params.append('userId', String(searchFilters.userId));
    }

    if (searchFilters.dataSourceId) {
      params.append('dataSourceId', String(searchFilters.dataSourceId));
    }

    if (searchFilters.status !== undefined) {
      params.append('status', String(searchFilters.status));
    }

    if (searchFilters.sortBy) {
      params.append('sortBy', searchFilters.sortBy);
    }

    // Pagination
    params.append('page', String(searchFilters.page || 1));
    params.append('limit', String(searchFilters.limit || 32));

    return appendPageLoadAt(appendShuffleSeed(params)).toString();
  }, []);

  /**
   * Execute search with Elasticsearch endpoint
   */
  const searchListings = useCallback(async (searchFilters: SearchFilters): Promise<SearchResult> => {
    setLoading(true);
    setError(null);

    try {
      const queryString = buildQueryString(searchFilters);
      const response = await fetch(`/api/search/listings?${queryString}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const rawData = await response.json();

      // Validate response against Zod schema
      const validatedData = validateSearchResponse(rawData);

      if (!validatedData.success) {
        throw new Error((validatedData as { success: false; error: string }).error);
      }

      // Update state
      setResults(validatedData.listings);
      setTotal(validatedData.total);
      setPage(validatedData.page);
      setLimit(validatedData.limit);
      setHasMore(validatedData.hasMore);
      setFiltersState(searchFilters);

      return validatedData;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Search failed';
      trackError(`[SearchProvider] Error: ${errorMsg}`, ErrorSeverity.WARNING);
      setError(errorMsg);

      // Return empty result on error
      return {
        success: true,
        listings: [],
        total: 0,
        page: searchFilters.page || 1,
        limit: searchFilters.limit || 32,
        hasMore: false
      };
    } finally {
      setLoading(false);
    }
  }, [buildQueryString]);

  /**
   * Search by tag with optional additional filters
   */
  const searchByTag = useCallback(async (
    tag: string,
    additionalFilters: Partial<SearchFilters> = {}
  ): Promise<SearchResult> => {
    const searchFilters: SearchFilters = {
      ...additionalFilters,
      tags: [tag],
      page: additionalFilters.page || 1,
      limit: additionalFilters.limit || 32
    };

    return searchListings(searchFilters);
  }, [searchListings]);

  /**
   * Load next page of results
   */
  const loadMore = useCallback(async () => {
    if (!hasMoreRef.current || loadingRef.current) {
      return;
    }

    const nextPage = pageRef.current + 1;
    const nextFilters = { ...filtersRef.current, page: nextPage };

    setLoading(true);
    setError(null);

    try {
      const queryString = buildQueryString(nextFilters);
      const response = await fetch(`/api/search/listings?${queryString}`);

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const rawData = await response.json();

      // Validate response against Zod schema
      const validatedData = validateSearchResponse(rawData);

      if (!validatedData.success) {
        throw new Error((validatedData as { success: false; error: string }).error);
      }

      // Append results
      setResults(prev => [...prev, ...validatedData.listings]);
      setPage(validatedData.page);
      setHasMore(validatedData.hasMore);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load more results';
      trackError(`[SearchProvider] Error loading more: ${errorMsg}`, ErrorSeverity.WARNING);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [buildQueryString]);

  /**
   * Clear search state
   */
  const clearSearch = useCallback(() => {
    setResults([]);
    setTotal(0);
    setPage(1);
    setLimit(32);
    setHasMore(false);
    setError(null);
    setFiltersState({});
  }, []);

  /**
   * Update filters without executing search
   */
  const setFilters = useCallback((newFilters: SearchFilters) => {
    setFiltersState(newFilters);
  }, []);

  // ============================================================================
  // Legacy ListingProvider functions
  // ============================================================================

  const getSearchList = useCallback(async (
    params: SearchFilters,
    replaceCurrentList?: (list: Record<string, unknown>[], params?: Record<string, unknown>) => void
  ) => {
    const { page = 1, categoryGroupId = 0, q = '', min = 0, max = 0, minPrice, maxPrice, itemCondition } = params;

    setSearchParamsState(params);

    const listingResponse = await fetchSearchList({
      categoryGroupId,
      q,
      min,
      max,
      minPrice,
      maxPrice,
      itemCondition,
      page
    });

    if (listingResponse && listingResponse.length > 0) {
      if (replaceCurrentList) {
        replaceCurrentList(pickDataForListing(listingResponse, 32), params as Record<string, unknown>);
      }
      setSearchEmpty(false);
    } else {
      setSearchEmpty(true);
      const fallbackParams = {
        categoryGroupId: categoryGroupId || 0,
        page: 1
      };
      const fallbackList = await fetchSearchList(fallbackParams);
      if (replaceCurrentList) {
        replaceCurrentList(pickDataForListing(fallbackList, 32), fallbackParams);
      }
    }
  }, []);

  const getFreshList = useCallback(async (
    params: SearchFilters,
    replaceCurrentList?: (list: Record<string, unknown>[], params?: Record<string, unknown>) => void
  ) => {
    const list = await fetchSearchList(params);
    if (list.length) {
      setSearchEmpty(false);
      if (replaceCurrentList) {
        const formattedList = pickDataForListing(list, 32);
        replaceCurrentList(formattedList, params as Record<string, unknown>);
      }
    } else {
      setSearchEmpty(true);
      const fallbackParams = {
        categoryGroupId: params.categoryGroupId || 0,
        page: 1
      };
      const fallbackList = await fetchSearchList(fallbackParams);
      if (replaceCurrentList) {
        replaceCurrentList(pickDataForListing(fallbackList, 32));
      }
    }
  }, []);

  const getFreshTagList = useCallback(async (
    tag: string,
    params: Partial<SearchFilters> = {},
    replaceCurrentList?: (list: Record<string, unknown>[], params?: Record<string, unknown>) => void
  ) => {
    try {
      const queryParams = new URLSearchParams({
        tag,
        page: String(params.page || 1),
        limit: '32',
      });

      if (params.categoryGroupId) {
        queryParams.append('categoryGroupId', String(params.categoryGroupId));
      }
      if (params.minPrice !== undefined) {
        queryParams.append('minPrice', String(params.minPrice));
      }
      if (params.maxPrice !== undefined) {
        queryParams.append('maxPrice', String(params.maxPrice));
      }
      if (params.itemCondition && Array.isArray(params.itemCondition)) {
        queryParams.append('itemCondition', params.itemCondition.join(','));
      }
      if (params.q) {
        queryParams.append('q', params.q);
      }
      if (params.sortBy) {
        queryParams.append('sortBy', params.sortBy);
      }

      const response = await fetch(`/api/search/tags?${appendShuffleSeed(queryParams)}`);
      const rawData = await response.json();

      // Validate response against Zod schema
      const parseResult = TagSearchResponseSchema.safeParse(rawData);
      if (!parseResult.success) {
        trackError(`[SearchProvider] Tag search validation failed: ${JSON.stringify(parseResult.error.flatten())}`, ErrorSeverity.WARNING);
        setSearchEmpty(true);
        if (replaceCurrentList) {
          replaceCurrentList([]);
        }
        return;
      }

      const data = parseResult.data;
      if (data.success && data.listings && data.listings.length > 0) {
        setSearchEmpty(false);
        if (replaceCurrentList) {
          replaceCurrentList(pickDataForListing(data.listings, 32));
        }
      } else {
        setSearchEmpty(true);
        if (replaceCurrentList) {
          replaceCurrentList([]);
        }
      }
    } catch (err) {
      trackError(err instanceof Error ? err : `[SearchProvider] Error fetching tag list: ${String(err)}`, ErrorSeverity.WARNING);
      setSearchEmpty(true);
      if (replaceCurrentList) {
        replaceCurrentList([]);
      }
    }
  }, []);

  const setSearchParamsFunc = useCallback((params: SearchFilters) => {
    setSearchParamsState((prevParams) => {
      const newParams: Record<string, unknown> = { ...prevParams };

      Object.keys(params).forEach(key => {
        if ((params as Record<string, unknown>)[key] === undefined || (params as Record<string, unknown>)[key] === null) {
          delete newParams[key];
        } else {
          newParams[key] = (params as Record<string, unknown>)[key];
        }
      });

      // Save to history
      setSearchHistory(prev => {
        const isDuplicate = prev.some(h => JSON.stringify(h) === JSON.stringify(newParams));
        if (!isDuplicate) {
          const newHistory = [...prev.slice(-19), newParams as SearchFilters];
          LS_Set('searchHistory', newHistory);
          return newHistory;
        }
        return prev;
      });

      const updatedState: Record<string, unknown> = {};
      Object.keys(newParams).forEach(key => {
        if (newParams[key] !== undefined && newParams[key] !== null) {
          updatedState[key] = newParams[key];
        }
      });
      return updatedState as SearchFilters;
    });
  }, []);

  const setRecentSearch = useCallback((params: SearchFilters) => {
    const normalizedParams = normalizeSearch(params);

    if (!isValidSearch(normalizedParams)) {
      return;
    }

    setRecentSearchesState(prevSearches => {
      const searchKey = recentSearchKey(normalizedParams);
      let searches = prevSearches.filter(s => recentSearchKey(s) !== searchKey);

      searches.unshift(normalizedParams);
      searches = searches.slice(0, 10);
      LS_Set('recentSearches', searches);

      return searches;
    });
  }, []);

  const setRecentCategory = useCallback((category: RecentCategory) => {
    if (category.id === 0) return;

    setRecentCategoriesState(prevCategories => {
      // Score-based ordering: each click bumps the category's count and the
      // list sorts by count, so a single click never jumps a category above
      // the user's most-clicked favorites.
      const existing = prevCategories.find(c => c.id === category.id);
      const updated = existing
        ? prevCategories.map(c =>
            c.id === category.id
              ? { ...c, ...category, count: c.count + 1 }
              : c
          )
        : [...prevCategories, { ...category, count: 1 }];

      const categories = [...updated].sort((a, b) => b.count - a.count);
      LS_Set('recentCategories', categories);

      return categories;
    });
  }, []);

  const removeRecentSearch = useCallback((query: string) => {
    setRecentSearchesState(prevSearches => {
      const searches = prevSearches.filter(s => s.q !== query);
      LS_Set('recentSearches', searches);
      return searches;
    });
  }, []);

  const removeRecentCategory = useCallback((id: number) => {
    setRecentCategoriesState(prevCategories => {
      const categories = prevCategories.filter(c => c.id !== id);
      LS_Set('recentCategories', categories);
      return categories;
    });
  }, []);

  const fetchJobList = useCallback(async (_q?: string, _page?: number) => {
    // Jobs feature removed
    return [];
  }, []);

  const getUserListings = useCallback(async (userId: string, productId: string, page?: number) => {
    return await fetchOtherListings(userId, productId, page);
  }, []);

  const value: SearchContextType = {
    // Modern search state
    results,
    total,
    page,
    limit,
    hasMore,
    loading,
    error,
    filters,

    // Legacy search state
    searchParams,
    searchHistory,
    searchEmpty,
    currentCategoryGroupId,
    recentSearches,
    recentCategories,

    // Modern actions
    searchListings,
    searchByTag,
    clearSearch,
    loadMore,
    setFilters,

    // Legacy actions
    getSearchList,
    getFreshList,
    getFreshTagList,
    setSearchParams: setSearchParamsFunc,
    setSearchEmpty,
    setCurrentCategoryGroupId,
    setRecentSearch,
    setRecentCategory,
    removeRecentSearch,
    removeRecentCategory,
    fetchJobList,
    getUserListings,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
};

export default SearchProvider;
